import { memo, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';

import { bridgeAtom, errorAtom, sessionAtom, useSetValue, useValue } from '@/store';

import { addProductToCart, addShopifyEvent } from '@/modules/api';
import { translations } from '@/modules/common';
import { ErrorType, EventType } from '@/modules/models';

import { MessageTextWrapper } from './MessageText.styles';

interface BubbleProps {
  text: string;
  isUserMessage: boolean;
  direction?: 'ltr' | 'rtl';
}

export const MessageText = memo<BubbleProps>((props) => {
  const { isUserMessage, text, direction = 'ltr' } = props;

  const session = useValue(sessionAtom);
  const { onAddToCartFromChat, shopifyAddToCartParams } = useValue(bridgeAtom);
  const setError = useSetValue(errorAtom);

  const [isSingleLine, setIsSingleLine] = useState(false);
  const bubbleRef = useRef<HTMLDivElement | null>(null);

  const addToCart = useCallback(
    async (event: MouseEvent) => {
      const element = event.target as HTMLElement;
      try {
        const productId = element.dataset?.productid as string;
        element.innerHTML = '<div class="spinner"></div>';
        const product = await addProductToCart(productId, 1, session, shopifyAddToCartParams);
        onAddToCartFromChat?.(product);
      } catch (error) {
        console.log(error);
        setError(ErrorType.AddToCartFailure);
      } finally {
        element.innerHTML = parse(translations.addToCartButton) as string;
      }
    },
    [session, setError, onAddToCartFromChat, shopifyAddToCartParams]
  );

  const checkItOut = useCallback(
    async (event: MouseEvent) => {
      const element = event.target as HTMLAnchorElement;
      const url = element.href;

      event.preventDefault();

      if (session) {
        const productId = element.dataset?.productid as string;

        await addShopifyEvent({
          userSession: session,
          type: EventType.OPEN_PRODUCT_FROM_CHAT,
          productId,
          quantity: 1,
        });
      }

      window.location.href = url;
    },
    [session]
  );

  const handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    switch (true) {
      case target?.matches('.add-to-cart'):
        return addToCart(event);

      case target?.matches('.check-out'):
        return checkItOut(event);
    }
  };

  useEffect(() => {
    const checkMultiline = () => {
      if (!bubbleRef.current) {
        return;
      }

      const computedStyle = window.getComputedStyle(bubbleRef.current);
      const paddingTop = parseFloat(computedStyle.paddingTop);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const contentHeight = bubbleRef.current.clientHeight - paddingTop - paddingBottom;

      setIsSingleLine(contentHeight <= lineHeight * 2.5);
    };

    checkMultiline();
    window.addEventListener('resize', checkMultiline);

    return () => {
      window.removeEventListener('resize', checkMultiline);
    };
  }, []);

  const shouldBreakWords = text.split(' ').length === 1;
  const formattedText = parse(text);

  return (
    <MessageTextWrapper
      ref={bubbleRef}
      isUserMessage={isUserMessage}
      shouldBreakWords={shouldBreakWords}
      isSingleLine={isSingleLine}
      direction={direction}
      onClick={handleClick}
    >
      {formattedText}
    </MessageTextWrapper>
  );
});

MessageText.displayName = 'MessageText';
